import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, Mail, KeyRound, Copy } from "lucide-react";
import toast from "react-hot-toast";
import api, { formatApiError } from "../api";
import BrandMark from "../components/BrandMark";
import LanguageToggle from "../components/LanguageToggle";
import { Button, Card } from "../components/ui/Primitives";

export default function WelcomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | error
  const [email, setEmail] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      setError("No session_id");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX = 20; // ~40s

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.status === "paid") {
          setStatus("paid");
          setEmail(data.email || "");
          setSetupToken(data.setup_token || "");
          setIsNew(!!data.is_new_user);
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts >= MAX) {
          setStatus("error");
          setError(t("welcome.timeout"));
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(formatApiError(e));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, t]);

  const copyToken = () => {
    navigator.clipboard?.writeText(setupToken);
    toast.success(t("welcome.token_copied"));
  };

  return (
    <div className="min-h-screen bg-bg-void text-ink-primary app-grain flex flex-col">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <LanguageToggle />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <Card className="p-8 md:p-10 max-w-xl w-full" data-testid="welcome-page">
          {status === "polling" && (
            <div className="text-center">
              <Loader2 size={40} className="text-brand animate-spin mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-ink-primary mb-2">{t("welcome.polling_title")}</h1>
              <p className="text-sm text-ink-secondary">{t("welcome.polling_sub")}</p>
            </div>
          )}

          {status === "paid" && (
            <div>
              <div className="flex items-center justify-center mb-5">
                <div className="w-14 h-14 rounded-full bg-ok/10 border border-ok/30 flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-ok" data-testid="welcome-paid-icon" />
                </div>
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink-primary text-center mb-2" data-testid="welcome-title">
                {isNew ? t("welcome.account_created") : t("welcome.account_attached")}
              </h1>
              <p className="text-sm text-ink-secondary text-center mb-6 leading-relaxed">
                {isNew ? t("welcome.check_email") : t("welcome.upgrade_attached_sub")}
              </p>

              {email && (
                <div className="bg-bg-void/60 border border-white/10 rounded-lg px-4 py-3 mb-4 flex items-center gap-3">
                  <Mail size={16} className="text-brand shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("welcome.your_email")}</div>
                    <div className="text-sm text-ink-primary font-medium truncate" data-testid="welcome-email">{email}</div>
                  </div>
                </div>
              )}

              {setupToken && (
                <div className="bg-warn/5 border border-warn/20 rounded-lg p-4 mb-5" data-testid="welcome-mocked-token-box">
                  <div className="flex items-center gap-2 mb-2">
                    <KeyRound size={14} className="text-warn" />
                    <span className="text-xs uppercase tracking-wider text-warn font-semibold">
                      {t("welcome.dev_token_label")}
                    </span>
                  </div>
                  <p className="text-xs text-ink-secondary mb-3 leading-relaxed">
                    {t("welcome.dev_token_sub")}
                  </p>
                  <div className="flex items-center gap-2 bg-bg-void/80 border border-white/10 rounded-md px-3 py-2 font-mono text-xs text-ink-primary break-all">
                    <span className="flex-1" data-testid="welcome-setup-token">{setupToken}</span>
                    <button onClick={copyToken} className="text-ink-secondary hover:text-ink-primary" data-testid="welcome-copy-token">
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                {setupToken && (
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={() => navigate(`/set-password?token=${encodeURIComponent(setupToken)}&new=1`)}
                    data-testid="welcome-set-password-btn"
                  >
                    <KeyRound size={14} />
                    {t("welcome.set_password_cta")}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => navigate("/login")}
                  data-testid="welcome-go-login"
                >
                  {t("welcome.go_login")}
                </Button>
              </div>
            </div>
          )}

          {(status === "failed" || status === "error") && (
            <div className="text-center">
              <AlertTriangle size={40} className="text-err mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-ink-primary mb-2">{t("welcome.error_title")}</h1>
              <p className="text-sm text-ink-secondary mb-5">{error || t("welcome.error_sub")}</p>
              <Button variant="secondary" onClick={() => navigate("/")} data-testid="welcome-back-home">
                {t("welcome.back_home")}
              </Button>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
